<template>
  <m-field
    :data-testid="testId"
    class="field-wrapper"
    :class="[{ 'flex-1': !width, 'is-multiple': isMultiple }, `doc-upload-${$.uid}`]"
    :status="hasError ? 'danger' : 'default'">
    <m-field-label v-if="label" slot="label" :data-testid="`${testId}-label`">
      {{ label }}

      <help-tooltip v-if="hint" :text="hint" />
    </m-field-label>
    <m-group>
      <template v-if="shouldDisplayUploadButton">
        <div class="button-wrapper" :key="renderKey">
          <secondary-button
            :is-loading="isLoading"
            test-id="file-upload-btn"
            leading-icon="upload"
            :disabled="isButtonDisabled"
            is-block
            @click="handleFileImport">
            {{ uploadButtonLabel }}
          </secondary-button>
          <input
            ref="uploader"
            class="file-input"
            type="file"
            @change="onFileChanged"
            :multiple="isMultiple" />
        </div>
      </template>
      <m-grid gap="spacing-1" class="tag-group">
        <span class="tag-wrapper" v-for="(fileKey, fileIndex) in fileKeys" :key="fileKey">
          <m-tag>
            <basic-icon class="p-r-1" color="color-text-tertiary">file-outline</basic-icon>
            <a
              v-if="isSafeCustomFieldDocumentUrl(fileUrls[fileIndex])"
              :href="fileUrls[fileIndex]"
              target="_blank"
              rel="noopener noreferrer"
              class="file-link">
              {{ getFileDisplayName(fileKey) }}
            </a>
            <span v-else>{{ getFileDisplayName(fileKey) }}</span>
            <basic-icon
              class="p-r-1 remove-file"
              color="color-text-tertiary"
              @click="removeFile(fileKey)">
              close
            </basic-icon>
          </m-tag>
        </span>
      </m-grid>
    </m-group>

    <m-field-hint :data-testid="`${testId}-errors`">
      <m-stack align="start" gap="spacing-0-5">
        <span v-if="shouldDisplayMessages">{{ errorBucket.join(', ') }}</span>
        <span>{{ computedFieldHint }}</span>
      </m-stack>
    </m-field-hint>
  </m-field>
</template>

<script>
import fieldMixin from '@/components/mixins/fieldMixin';
import validatable from '@/components/mixins/validatable';
import testable from '@/components/mixins/testable';
import renderMixin from '@satellite/components/mixins/renderMixin';
import { SecondaryButton, BasicIcon, HelpTooltip } from '@/components';
import { isArray } from 'class-validator';
import {
  GlobalLimits,
  getDocumentNameFromUrl,
  isSafeCustomFieldDocumentUrl,
  isImageUrl,
  convertBytes,
  FileUnits
} from '@nova/core';

/**
 * Document upload button/display
 * @displayName Document Upload
 */
export default {
  name: 'DocumentUpload',
  mixins: [fieldMixin, renderMixin, testable, validatable],
  components: { SecondaryButton, BasicIcon, HelpTooltip },
  props: {
    /**
     * @model
     */
    value: {
      type: [String, Array],
      required: false,
      default: () => []
    },
    /**
     * Allows multiple document uploads
     */
    isMultiple: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      mounted: false,
      isLoading: false,
      fileUrls: [],
      fileKeys: []
    };
  },
  computed: {
    selectedFiles() {
      return (isArray(this.value) ? this.value : [this.value]).filter(file => file);
    },
    uploadButtonLabel() {
      return this.$t('global.phrases.addFiles', this.isMultiple);
    },
    computedFieldHint() {
      return this.isMultiple
        ? this.$t(
            'appointment.forms.appointmentDetailsForm.messages.imageTextSpreadsheetOrZipFilesAllowedUpToFilesWithAMaximumSizeOfEach',
            {
              maxFiles: GlobalLimits.MULTI_DOC_MAX_FILES_PER_APPOINTMENT.value,
              maxSize: this.maxUploadSize
            }
          )
        : `${this.$t(
            'appointment.forms.appointmentDetailsForm.messages.oneImageTextSpreadsheetOrZipAllowedMaxSize'
          )}: ${this.maxUploadSize}`;
    },
    shouldDisplayUploadButton() {
      // Use internal fileKeys state (not the value prop) so visibility stays
      // correct after files are added or removed post-mount.
      return this.isMultiple || this.fileKeys.length === 0;
    },
    isButtonDisabled() {
      return (
        this.disabled ||
        (this.isMultiple
          ? this.fileKeys.length >= GlobalLimits.MULTI_DOC_MAX_FILES_PER_APPOINTMENT.value
          : false)
      );
    },
    maxUploadSize() {
      return `${convertBytes(GlobalLimits.MAX_STORAGE_FILE_SIZE_BYTES.value, FileUnits.MB)}${
        FileUnits.MB
      }`;
    }
  },
  methods: {
    /**
     * Determines if file is image for thumbnail display purposes
     * @returns {*|boolean}
     */
    isImageFile(fileKey) {
      return isImageUrl(fileKey);
    },
    /**
     * Handles file upload button click and sets button back to initial state after upload window is closed
     * @public
     */
    fileUploadClick() {
      window.addEventListener(
        'focus',
        () => {
          this.isLoading = false;
        },
        { once: true }
      );

      this.$refs.fileinput.$refs.input.click();
    },
    /**
     * Handles file selection change
     * @public
     * @returns {Promise<void>}
     */
    async onFileChanged(e) {
      const newFiles = e.target.files;
      const remainingSlots =
        GlobalLimits.MULTI_DOC_MAX_FILES_PER_APPOINTMENT.value - this.fileKeys.length;

      if (newFiles.length > remainingSlots) {
        this.notify(
          this.$t('appointment.forms.appointmentDetailsForm.messages.maximumOfFilesAllowed', {
            maxFiles: GlobalLimits.MULTI_DOC_MAX_FILES_PER_APPOINTMENT.value
          }),
          'error'
        );

        return;
      }

      this.$emit('uploading');
      this.isLoading = true;
      if (newFiles.length > 0) {
        try {
          const formData = new FormData();
          Array.from(newFiles).forEach(file => {
            formData.append('files', file);
          });

          const response = await axios.post('/storage/files', formData);

          // Response is an array of uploaded file objects
          response.data.forEach(fileData => {
            this.fileUrls.push(fileData.url);
            this.fileKeys.push(fileData.key);
          });
        } finally {
          this.isLoading = false;
        }
      }

      this.internalValue = this.fileKeys;
      this.$emit('uploaded');
    },
    removeFile(fileKey) {
      this.fileUrls = this.fileUrls.filter(url => !url.endsWith(fileKey));
      this.fileKeys = this.fileKeys.filter(key => key !== fileKey);
      this.internalValue = this.fileKeys;
    },
    handleFileImport() {
      this.isLoading = true;

      const resetLoading = () => {
        this.isLoading = false;
        window.removeEventListener('focus', resetLoading);
        this.$refs.uploader?.removeEventListener('cancel', resetLoading);
      };

      // Primary: fires when the browser window regains focus after the OS file picker closes
      window.addEventListener('focus', resetLoading);

      // Fallback: the 'cancel' event fires when the user dismisses the file picker
      // without selecting a file (Chrome 113+, Firefox 122+). Handles environments
      // where window.focus is not reliably dispatched (e.g. Linux/Wayland, modal dialogs).
      this.$refs.uploader?.addEventListener('cancel', resetLoading, { once: true });

      this.$refs.uploader?.click();
    },
    isSafeCustomFieldDocumentUrl,
    getFileDisplayName(fileKey) {
      // NOTE:
      // The appended icon has to be absolutely positioned so it is not cut off when the tag component's max-width is reached
      // We add three spaces to prevent appended icon from obscuring the file name and to match the prepended icon spacing
      return `${getDocumentNameFromUrl(fileKey)}${'\u00A0\u00A0\u00A0'}`;
    }
  },
  mounted() {
    this.fileUrls = this.value ? (isArray(this.value) ? this.value : [this.value]) : [];

    // Set default value for the v-model
    if (this.fileUrls?.length) {
      this.fileKeys = this.fileUrls.map(value => {
        try {
          return new URL(value).pathname.slice(1);
        } catch {
          return value;
        }
      });
    }

    this.mounted = true;

    if (this.fileKeys.length > 0) {
      this.internalValue = this.fileKeys;
    }
  },
  watch: {
    isButtonDisabled() {
      this.rerender();
    },
    value: {
      handler() {
        this.$nextTick(() => {
          const nodes = document.querySelectorAll(`.doc-upload-${this.$.uid} m-tag:not(.adjusted)`);
          this.util.appendStylesToShadowDomEls(
            nodes,
            '.tag',
            'width:100%;padding:8px 20px 8px 12px !important;'
          );
          nodes.forEach(node => node.classList.add('adjusted'));
        });
      },
      immediate: true
    }
  }
};
</script>

<style scoped lang="scss">
.file-input {
  display: none;
}
.tag-wrapper {
  position: relative;
}
.file-link {
  color: inherit;
  text-decoration: underline;
}
.button-wrapper {
  width: 112px;
}
.tag-group {
  max-width: 405px;
}

.is-multiple {
  .tag-group {
    grid-template-columns: repeat(2, 1fr);
  }
}
.remove-file {
  position: absolute;
  background-color: $m-color-background-tertiary;
  padding: 0;
  top: 9px;
  right: 8px;
  border-radius: 50%;

  &:hover {
    cursor: pointer;
  }
}
</style>
